//

import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Typography, Avatar, Collapse, MdiIcon, IconButton } from '@mic3/platform-ui';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

DOMPurify.setConfig({ ADD_ATTR: ['target'] });

import { chooseIcon } from 'app/utils/attachments/attachmentsUtils';
import { getAttachmentUrl } from 'app/utils/attachments/attachmentsUtils';
import { parseMessage, formatChatTimestamp } from 'app/utils/chat/chatUtils';
import { THREADS_ENABLED } from 'app/config/chatConfig';
import { escapeEmptyMarkdownWrappers } from 'app/utils/markdown/markdownUtils';
import { messageMarkdownStyles, codeMirrorThemeClass } from 'app/utils/markdown/messageMarkdownStyles';
import { loadAvatar } from 'store/actions/app/appActions';
import { openThreadView } from 'store/actions/chat/chatThreadActions';
import { useToggle } from 'app/utils/hook/hooks';
import { rocketHost } from 'app/utils/env';
import { isEmpty } from 'app/utils/utils';
import { get } from 'app/utils/lo/lo';

import ListItem from 'app/components/molecules/List/ListItem';
import Tooltip from 'app/components/atoms/Tooltip/Tooltip';
import Image from 'app/components/atoms/Image/Image';
import Icon from 'app/components/atoms/Icon/Icon';
import Flex from 'app/components/atoms/Flex/Flex';
import MessageReactions from 'app/components/organisms/Chat/MessageReactions';
import MessageReactionPicker from 'app/components/organisms/Chat/MessageReactionPicker';
import ThreadSummary, { MessageBubble } from 'app/components/organisms/Chat/ThreadSummary';
import ChatItemHeader, { MessageBodyHeader } from 'app/components/organisms/Chat/ChatItemHeader';
import { useChatItemActions } from 'app/utils/chat/hooks/useChatItemActions';
import { useMessageReactions } from 'app/utils/chat/hooks/useMessageReactions';
import { useDeleteMessageConfirmation } from 'app/utils/chat/hooks/useDeleteMessageConfirmation';

const Link = styled.a`
    text-decoration: none;
`;

const MessageItemStyle = styled.div`
    display: flex;
    clear: both;
    max-width: 100%;
    margin-bottom: ${({ type }) => (!type ? '24px' : '8px')};
    & .message-actions {
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s;
    }
    & .message-read-tick-slot {
        opacity: 1;
        transition: opacity 0.15s;
    }
    &:hover .message-actions,
    & .message-actions.is-open {
        opacity: 1;
        pointer-events: auto;
    }
    &:hover .message-read-tick-slot,
    &:has(.message-actions.is-open) .message-read-tick-slot {
        opacity: 0;
        pointer-events: none;
    }
`;

const MessageContent = styled.div`
    position: relative;
    font-size: 0.9em;
    display: block;
    word-break: break-word;
    color: ${({ theme }) => theme.material.colors.text.primary};
    p {
        margin: 0;
    }
    width: calc(100% - 50px);

    .checkboxes {
        list-style-type: none;
        padding-inline-start: 4px;
    }

    & ul {
        padding-left: 25px;
    }

    img {
        max-width: 100%;
    }

    code {
        white-space: break-spaces;
    }

    & p {
        white-space: pre-wrap;
    }

    & .mention-link-group {
        pointer-events: none;
        cursor: default;
        transition: opacity 0.3s, background-color 0.3s, color 0.3s;
        font-weight: 700;
        padding: 0 2px 2px;
        border-radius: 4px;
        background-color: #fde8d7;
        color: #f38c39;
    }

    & .mention-link-me {
        pointer-events: none;
        cursor: default;
        transition: opacity 0.3s, background-color 0.3s, color 0.3s;
        font-weight: 700;
        padding: 0 2px 2px;
        border-radius: 4px;
        background-color: #f5455c;
        color: #ffe9ec;
    }

    & .mention-link-user {
        pointer-events: none;
        cursor: default;
        transition: opacity 0.3s, background-color 0.3s, color 0.3s;
        font-weight: 700;
        padding: 0 2px 2px;
        border-radius: 4px;
        background-color: #fff6d6;
        color: #b68d00;
    }
`;

const MessageText = styled.div.attrs(({ theme }) => ({ className: codeMirrorThemeClass(theme) }))`
    background: ${({ editActive, theme }) => (editActive ? theme.material.colors.background.active : theme.material.colors.background.paper)};
    border-radius: 6px;
    /* The cm-s-idea (light) code-highlight theme forces Consolas/monospace on this whole
       container — restore the inherited app font for normal text; code/pre keep mono below. */
    && {
        font-family: inherit;
    }
    ${({ padding }) => padding && `padding: ${padding};`}
    & ol {
        padding-left: 23px;
    }

    & .MuiTypography-caption {
        display: block;
    }
    & p {
        color: ${({ theme }) => theme.material.colors.text.primary};
    }
    & .chat-emoji-glyph {
        font-size: 24px;
        line-height: 1;
        vertical-align: middle;
    }
    & .chat-emoji-glyph--big {
        font-size: 44px;
    }
    ${messageMarkdownStyles}
`;

const SysMsgBodyWrapper = styled.div`
    ${({ editActive, theme }) => editActive && `background: ${theme.material.colors.background.active};`}
    display: flex;
    align-items: center;
    & .system-msg {
        font-style: italic;
        font-size: 12px;
        color: ${({ theme }) => theme.material.colors.text.primary};
    }
    & .system-msg em {
        font-weight: 600;
    }
    @media (max-width: 961px) {
        display: block;
    }
`;

const AvatarWrap = styled(Tooltip)`
    margin: ${({ type }) => (!type ? '12px .5rem 0' : '0 .5rem')};
    @media (max-width: 961px) {
        margin: 12px 0.5rem 0;
    }
`;

const AvatarSmall = styled(Avatar)`
    width: ${({ type }) => (!type ? '32px !important' : '20px !important')};
    height: ${({ type }) => (!type ? '32px !important' : '20px !important')};
    && {
        ${({ type }) => (type ? `font-size: 12px; font-style: normal; font-weight: 500;` : '')};
    }
`;

const AttachmentImage = styled(Image)`
    max-width: 100%;
    cursor: pointer;
`;

const IconButtonStyled = styled(IconButton)`
    padding: 6px !important;
`;

const ListItemStyled = styled(ListItem)`
    margin: 0 auto;
    padding: 0;
    box-shadow: none;
    border-radius: 6px;
`;

// const AttachmentText = styled.span`
//     ${({ deleted }) => deleted ? 'text-decoration: line-through;' : ''}
//     white-space: nowrap;
//     overflow: hidden;
//     text-overflow: ellipsis;
//     max-width: 100%;
// `;

// const MenuWrapper = styled.div`
//     position: absolute;
//     top: 0;
//     right: 0;
// `;

const AttachmentLink = memo(props => (
    <Link target="_blank" href={props.href}>
        {props.children}
    </Link>
));

const AttachmentIcon = memo(props => {
    const { mimeType, href, filename } = props;
    const iconName = chooseIcon(mimeType, filename);
    return (
        <AttachmentLink href={href}>
            <Icon name={iconName} size="lg" />
        </AttachmentLink>
    );
});

const FlexStyled = styled(Flex)`
    flex-wrap: wrap;
    margin-top: 10px;
`;

const FileTitle = styled(Typography)`
    flex: 1;
`;


const AliveChatItem = ({
    userChatId,
    message,
    loadAvatar,
    avatar,
    userId,
    openImagePreview,
    entityType,
    profile,
    editActive,
    onEdit,
    isRoomOwner,
    isReadOnly,
    rid,
    customEmojis,
    onShowReceipts,
    insideThread = false,
    openThreadView,
}) => {
    const [isExpanded, toggleExpansion] = useToggle(true);
    const [text, setText] = useState('');
    const { reactions, currentUsername, onToggle } = useMessageReactions(message, rid);
    const {
        file,
        createDate,
        editedDate,
        editedBy: { username: userEditor } = {},
        user: { name, username },
        type: messageType,
        mentions,
        unread,
    } = message;

    const { primary } = profile || {};
    const isAuthor = username === (primary && primary.username);
    const canEdit = isAuthor && isEmpty(file);
    const { canDelete, requestDelete, deleteDialog } = useDeleteMessageConfirmation({ rid, isAuthor, isRoomOwner });
    const canReact = !isReadOnly;
    const canSeeReceipts = !messageType;
    const onReplyInThread = useCallback(() => openThreadView(rid, message), [openThreadView, rid, message]);
    const onOpenThread = useCallback(msg => openThreadView(rid, msg), [openThreadView, rid]);
    const { actionsRef, pickerAnchor, openPicker, closePicker, handlePickerSelect, menuItems, onDotMenuClick } = useChatItemActions({
        message,
        onToggle,
        onEdit,
        onDelete: requestDelete,
        onShowReceipts,
        onReplyInThread,
        canEdit,
        canDelete,
        canReact,
        canSeeReceipts,
        canThread: THREADS_ENABLED && !messageType && !insideThread,
    });

    const itemHeader = (
        <ChatItemHeader
            name={name}
            createDate={createDate}
            editedDate={editedDate}
            userEditor={userEditor}
            actionsRef={actionsRef}
            pickerAnchor={pickerAnchor}
            openPicker={openPicker}
            menuItems={menuItems}
            onDotMenuClick={onDotMenuClick}
            canReact={canReact}
            unread={unread}
        />
    );

    useEffect(() => {
        const { primary } = profile || {};
        parseMessage(message?.text, mentions, primary, newMessage => {
            setText(newMessage);
        });
    }, []); // eslint-disable-line

    useEffect(() => {
        if (text && text !== message.text) {
            const { primary } = profile || {};
            parseMessage(message?.text, mentions, primary, newMessage => {
                setText(newMessage);
            });
        }
    }, [message, mentions, profile, text]);

    useEffect(() => {
        if (isEmpty(avatar)) {
            loadAvatar(username, 'user');
        }
    }, [entityType, loadAvatar, username]); // eslint-disable-line

    const messageBody = useMemo(() => {
        if (!text && !file) return null;
        const preProcessedText = escapeEmptyMarkdownWrappers(text);
        let markedText = marked(preProcessedText, { sanitize: true });
        if (text?.includes('mention-link-group') || text?.includes('mention-link-user') || text?.includes('mention-link-me') || text?.includes('chat-emoji-glyph')) {
            markedText = marked(preProcessedText);
        }
        const updatedText = DOMPurify.sanitize(markedText)
            .replace(/(<ul>)(\n<li><input (checked="" )?disabled="" type="checkbox")/g, '<ul class="checkboxes">$2')
            .replace(/<(a href)(=".*")/g, '<a target="_blank" href$2');

        if (file) {
            const { id: fileId, name: fileName, type, description } = file || {};
            const fileUrl = `${document.location.protocol}//${rocketHost}/file-upload/${fileId}/${fileName}?download`;
            const src = `/chat/file-upload/${fileId}/${fileName}`;
            switch (type) {
                case 'image/svg+xml':
                case 'image/gif':
                case 'image/jpeg':
                case 'image/jpg':
                case 'image/png':
                    return (
                        <MessageText padding="8px 16px" editActive={editActive}>
                            {itemHeader}
                            <Collapse in={isExpanded} collapsedHeight="40px">
                                <FlexStyled spaceBetween alignItems="center">
                                    <FileTitle variant="subtitle2" noWrap>
                                        <AttachmentLink href={fileUrl}>{fileName}</AttachmentLink>
                                    </FileTitle>
                                    <Flex alignItems="center">
                                        <AttachmentLink href={fileUrl}>
                                            <IconButtonStyled>
                                                <MdiIcon name="download" fontSize="small" />
                                            </IconButtonStyled>
                                        </AttachmentLink>
                                        <IconButtonStyled onClick={toggleExpansion}>
                                            <MdiIcon name={isExpanded ? 'menu-up' : 'menu-down'} fontSize="small" />
                                        </IconButtonStyled>
                                    </Flex>
                                </FlexStyled>
                                <div>
                                    <AttachmentImage alt={text} src={src} onClick={() => openImagePreview(fileId)} />
                                    {description && <Typography variant="caption">{description}</Typography>}
                                </div>
                            </Collapse>
                        </MessageText>
                    );
                default:
                    return (
                        <MessageText padding="8px 16px" editActive={editActive}>
                            {itemHeader}
                            <ListItemStyled
                                component={<AttachmentIcon filename={fileName} mimeType={type} href={fileUrl} />}
                                title={
                                    <FlexStyled spaceBetween alignItems="center">
                                        <FileTitle variant="subtitle2" noWrap>
                                            <AttachmentLink href={fileUrl}>{fileName}</AttachmentLink>
                                        </FileTitle>
                                        <Flex alignItems="center">
                                            <AttachmentLink href={fileUrl}>
                                                <IconButtonStyled>
                                                    <MdiIcon name="download" fontSize="small" />
                                                </IconButtonStyled>
                                            </AttachmentLink>
                                        </Flex>
                                    </FlexStyled>
                                }
                                subTitle={<Typography variant="caption">{description}</Typography>}
                                raised
                                rowWrap
                            />
                        </MessageText>
                    );
            }
        }

        return (
            <>
                {!messageType && !updatedText && (
                    <MessageText padding="8px 16px" editActive={editActive}>
                        {itemHeader}
                        <p>{text}</p>
                    </MessageText>
                )}
                {!messageType && !!updatedText && (
                    <MessageText padding="8px 16px" editActive={editActive}>
                        {itemHeader}
                        <div dangerouslySetInnerHTML={{ __html: updatedText }} />
                    </MessageText>
                )}
                {messageType && (
                    <SysMsgBodyWrapper>
                        <MessageBodyHeader type={messageType} variant="body1">
                            {`${username} ${formatChatTimestamp(createDate)} `}
                        </MessageBodyHeader>
                        <div className="system-msg" dangerouslySetInnerHTML={{ __html: text }} />
                    </SysMsgBodyWrapper>
                )}
            </>
        );
    }, [text, file, messageType, editActive, isExpanded, pickerAnchor, unread]); // eslint-disable-line react-hooks/exhaustive-deps

    const { name: aName, image, id } = avatar || {};
    const imageSource = image ? getAttachmentUrl(id, 'user', image) : null;
    return (
        <MessageItemStyle type={messageType} editActive={editActive}>
            <AvatarWrap type={messageType} alt={aName} x={10} y={-50}>
                <span alt={aName}>
                    <AvatarSmall type={messageType} initials={aName} src={imageSource} />
                </span>
            </AvatarWrap>
            <MessageContent>
                {THREADS_ENABLED && !insideThread && message.tcount && !messageType ? (
                    <MessageBubble>
                        {messageBody}
                        <ThreadSummary message={message} onOpenThread={onOpenThread} />
                    </MessageBubble>
                ) : (
                    messageBody
                )}
                <MessageReactions reactions={reactions} currentUsername={currentUsername} onToggle={onToggle} customEmojis={customEmojis} canReact={canReact} />
                {canReact && (
                    <MessageReactionPicker
                        anchorEl={pickerAnchor}
                        open={Boolean(pickerAnchor)}
                        onClose={closePicker}
                        onSelect={handlePickerSelect}
                        customEmojis={customEmojis}
                    />
                )}
            </MessageContent>
            {deleteDialog}
        </MessageItemStyle>
    );
};

AliveChatItem.propTypes = {
    rid: PropTypes.string,
    message: PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string,
        text: PropTypes.string,
        createDate: PropTypes.instanceOf(Date),
        file: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            description: PropTypes.string,
        }),
        user: PropTypes.shape({
            id: PropTypes.string.isRequired,
            username: PropTypes.string.isRequired,
            name: PropTypes.string,
        }).isRequired,
    }).isRequired,
    openImagePreview: PropTypes.func.isRequired,
    profile: PropTypes.object.isRequired,
    customEmojis: PropTypes.array,
    isReadOnly: PropTypes.bool,
    onShowReceipts: PropTypes.func,
    insideThread: PropTypes.bool,
};

export default connect(
    (state, props) => ({
        profile: state.user.profile,
        userChatId: state.user.chat.id,
        customEmojis: get(state, 'chat.emojis.custom') || [],
        avatar: get(state.admin.users.references, 'data', []).find(usr => usr.username === props.message.user.username),
    }),
    { loadAvatar, openThreadView }
)(AliveChatItem);
